import { fetcherMap } from "@components/CampaignTable/utils";
import { AdsSalesApi } from "@services/ads-sales/ads-sales.api";
import { OrganicApi, OrganicMetrics } from "@services/organic/organic.api";

export interface FunnelAdsData {
  "Amazon Ads": number[];
  "Amazon Organic": number[];
  "Organic Social": number[];
  DSP: number[];
}

export interface FunnelData {
  baseData: FunnelAdsData;
  whatIfData: FunnelAdsData | null;
  allMetrics: Record<string, number>;
}

export class FunnelService {
  private static readonly adsSalesApi = new AdsSalesApi();
  private static readonly organicApi = new OrganicApi();

  private static readonly DSP_STATIC = [30, 2, 1];
  private static readonly ORGANIC_SOCIAL_STATIC = [50, 3, 2];

  private static async fetchAdsMetrics(
    date_start: string,
    date_end: string,
    isWhatIf: boolean,
    saturation: number,
    campaignsTab: string,
    saturationKeys: string[],
  ): Promise<Record<string, number>> {
    const method = isWhatIf
      ? fetcherMap[campaignsTab].bind(
          this.adsSalesApi,
          date_start,
          date_end,
          saturation,
          [],
          saturationKeys,
          true,
        )
      : this.adsSalesApi.getAllMetrics.bind(
          this.adsSalesApi,
          date_start,
          date_end,
          saturation,
        );

    let raw = await method();
    if (Array.isArray(raw)) raw = raw[0] ?? {};
    return raw as Record<string, number>;
  }

  private static async fetchOrganicMetrics(
    date_start: string,
    date_end: string,
    asin?: string,
  ): Promise<OrganicMetrics> {
    return this.organicApi.getOrganicMetrics(date_start, date_end, asin);
  }

  private static buildBaseData(
    adsMetrics: Record<string, number>,
    organicResp: OrganicMetrics,
  ): FunnelAdsData {
    return {
      "Amazon Ads": [
        adsMetrics.ad_impressions || 0,
        adsMetrics.ad_clicks || 0,
        adsMetrics.ad_units_sold || 0,
      ],
      "Amazon Organic": [
        organicResp.organicImpressions,
        organicResp.organicClicks,
        organicResp.organicUnitsSold,
      ],
      "Organic Social": this.ORGANIC_SOCIAL_STATIC,
      DSP: this.DSP_STATIC,
    };
  }

  private static buildWhatIfData(
    adsMetrics: Record<string, number>,
    isWhatIf: boolean,
    saturation: number,
  ): FunnelAdsData | null {
    if (!isWhatIf || saturation === 0) return null;

    const hasWhatIf = Object.keys(adsMetrics).some((key) =>
      key.endsWith("_what_if"),
    );
    if (!hasWhatIf) return null;

    return {
      "Amazon Ads": [
        adsMetrics.ad_impressions_what_if || 0,
        adsMetrics.ad_clicks_what_if || 0,
        adsMetrics.ad_units_sold_what_if || 0,
      ],
      "Amazon Organic": [
        adsMetrics.organic_impressions_what_if || 0,
        adsMetrics.organic_clicks_what_if || 0,
        adsMetrics.organic_units_sold_what_if || 0,
      ],
      "Organic Social": this.ORGANIC_SOCIAL_STATIC,
      DSP: this.DSP_STATIC,
    };
  }

  private static buildAllMetrics(
    adsMetrics: Record<string, number>,
    organicResp: OrganicMetrics,
  ): Record<string, number> {
    return {
      ...adsMetrics,
      organicVolume: organicResp.organicVolume,
      organicImpressions: organicResp.organicImpressions,
      organicClicks: organicResp.organicClicks,
      organicCartAdds: organicResp.organicCartAdds,
      organicUnitsSold: organicResp.organicUnitsSold,
      organicCvr: organicResp.organicCvr,
      organicCtr: organicResp.organicCtr,
    };
  }

  static readonly getFunnelData = async (
    date_start: string,
    date_end: string,
    isWhatIf: boolean = false,
    saturation: number = 0,
    campaignsTab: string = "Campaigns",
    saturationKeys: string[] = [],
    asin?: string,
  ): Promise<FunnelData> => {
    try {
      const adsMetrics = await this.fetchAdsMetrics(
        date_start,
        date_end,
        isWhatIf,
        saturation,
        campaignsTab,
        saturationKeys,
      );
      const organicResp = await this.fetchOrganicMetrics(
        date_start,
        date_end,
        asin,
      );

      const baseData = this.buildBaseData(adsMetrics, organicResp);
      const whatIfData = this.buildWhatIfData(adsMetrics, isWhatIf, saturation);
      const allMetrics = this.buildAllMetrics(adsMetrics, organicResp);

      return { baseData, whatIfData, allMetrics };
    } catch (error) {
      console.error("Error fetching funnel data:", error);

      const empty = [0, 0, 0];
      return {
        baseData: {
          "Amazon Ads": empty,
          "Amazon Organic": empty,
          "Organic Social": empty,
          DSP: empty,
        },
        whatIfData: null,
        allMetrics: {},
      };
    }
  };
}
